import { combineReducers } from 'redux';
import actionsCollection from '@app/actions-chain/actionsCollection';
import {
  STATE_EDITOR,
  RESET_CUSTOMIZE_DATA,
  PERFORM_ACTION,
  CLEANUP_ACTION,
  SET_SNAP2EDGE_VISIBLE,
  SET_SNAP2EDGE_ENABLED,
  SET_EDITOR_ZOOM,
  UPDATE_CUSTOMIZE_ACTION,
  UPDATE_CUSTOMIZE_ACTIONS,
  SET_TOOL_PANEL_OVERLAY_LAYOUT,
  CHANGE_MEASUREMENT,
  SET_RESIZE_PARAMS,
  SET_PANEL_SIZES,
  SET_SCALE_PARAMS,
  SET_BACKGROUND_PARAMS,
  SET_IMAGE_NOT_GENERATED,
  ADD_WORKING_AREA_WARNING,
  CLEAR_WORKING_AREA_WARNINGS,
  REMOVE_WORKING_AREA_WARNING,
  ADD_WORKING_AREA_MESSAGE,
  CLEAR_WORKING_AREA_MESSAGES,
  REMOVE_WORKING_AREA_MESSAGE,
} from '@app/actions/_types';

const initialCustomizeDataState = {
  editor: null,
  editorZoom: 1,
  snapToEdgeVisible: false,
  snapToEdgeEnabled: true,
  toolPanelOverlayLayout: null,
  measurementLocale: undefined,
  resizeParams: {
    isShapeChanged: false,
    isCornerTypeChanged: false,
    isSizeChanged: false,
    hasValidationErrors: false,
    selectedSizes: {},
    selectedShape: null,
    prevSelectedShape: null,
    shapeByCornerType: null,
    prevShapeByCornerType: null,
    currentCornerType: null,
    selectedCornerType: null,
    shouldApply: false,
    shouldReset: false,
    prevSizes: {},
    labelsPerSheet: null,
  },
  panelSizes: {},
  scaleParams: {
    aspectRatioLocked: true,
  },
  imageNotGenerated: true,
  workingAreaWarnings: [],
  workingAreaMessages: [],
  bgParams: {
    contourCut: false,
    contourCutChanged: false,
    bgVisibilityChanged: false,
    artworkBgVisible: true,
    removedBgImgName: '',
    removeBgInProgress: false,
    contourCutInProgress: false,
  },
};

export const customizeDataReducer = (state = initialCustomizeDataState, { type, payload } = {}) => {
  const { scaleParams, workingAreaWarnings, workingAreaMessages, resizeParams, bgParams } = state;

  switch (type) {
    case STATE_EDITOR:
      return { ...state, editor: payload };
    case RESET_CUSTOMIZE_DATA:
      return { ...initialCustomizeDataState };
    case SET_SNAP2EDGE_VISIBLE:
      return { ...state, snapToEdgeVisible: payload };
    case SET_SNAP2EDGE_ENABLED:
      return { ...state, snapToEdgeEnabled: payload };
    case SET_EDITOR_ZOOM:
      return { ...state, editorZoom: payload };
    case SET_TOOL_PANEL_OVERLAY_LAYOUT:
      return { ...state, toolPanelOverlayLayout: payload };
    case CHANGE_MEASUREMENT:
      return { ...state, measurementLocale: payload };
    case SET_RESIZE_PARAMS:
      return { ...state, resizeParams: { ...resizeParams, ...payload } };
    case SET_PANEL_SIZES:
      return { ...state, panelSizes: payload };
    case SET_SCALE_PARAMS:
      return { ...state, scaleParams: { ...scaleParams, ...payload } };
    case SET_BACKGROUND_PARAMS:
      return { ...state, bgParams: { ...bgParams, ...payload } };
    case SET_IMAGE_NOT_GENERATED:
      return { ...state, imageNotGenerated: payload };
    case ADD_WORKING_AREA_WARNING:
      return { ...state, workingAreaWarnings: [...workingAreaWarnings, payload] };
    case CLEAR_WORKING_AREA_WARNINGS:
      return { ...state, workingAreaWarnings: [] };
    case REMOVE_WORKING_AREA_WARNING: {
      const warningPresented = workingAreaWarnings.some(warning => warning.key === payload);
      return {
        ...state,
        workingAreaWarnings: warningPresented ? workingAreaWarnings.filter(warning => warning.key !== payload) : workingAreaWarnings,
      };
    }
    case ADD_WORKING_AREA_MESSAGE:
      return { ...state, workingAreaMessages: [...workingAreaMessages, payload] };
    case CLEAR_WORKING_AREA_MESSAGES:
      return { ...state, workingAreaMessages: [] };
    case REMOVE_WORKING_AREA_MESSAGE: {
      const messagePresented = workingAreaMessages.some(message => message.key === payload);
      return {
        ...state,
        workingAreaMessages: messagePresented ? workingAreaMessages.filter(message => message.key !== payload) : workingAreaMessages,
      };
    }
    default:
      return state;
  }
};

export const actionsReducer = (state = actionsCollection, { type, payload } = {}) => {
  const updateActions = (actions, data) =>
    actions.map(action => ({
      ...action,
      ...data[action.name],
    }));

  const disableGroups = actions => {
    const disabledGroups = {};
    actions.forEach(({ group, disabled }) => {
      group && !Object.hasOwn(disabledGroups, group) && (disabledGroups[group] = true);
      group && disabledGroups[group] && !disabled && (disabledGroups[group] = false);
    });
    return actions.map(action => ({ ...action, disabled: !!disabledGroups[action.name] || action.disabled }));
  };

  switch (type) {
    case UPDATE_CUSTOMIZE_ACTIONS: {
      const {
        fieldTranslate: { disabled: fieldTranslateDisabled = true },
        rotateSelectedFields: { disabled: rotateSelectedFieldsDisabled = true },
      } = payload;

      state = state.map(({ name: actionName }) => {
        const baseAction = actionsCollection.find(({ name }) => name === actionName);
        return { ...baseAction, ...(payload[actionName] || {}) };
      });
      state = disableGroups(state);

      return updateActions(state, {
        translateUp: { disabled: fieldTranslateDisabled },
        translateDown: { disabled: fieldTranslateDisabled },
        translateLeft: { disabled: fieldTranslateDisabled },
        translateRight: { disabled: fieldTranslateDisabled },
        translateUpSmall: { disabled: fieldTranslateDisabled },
        translateDownSmall: { disabled: fieldTranslateDisabled },
        translateLeftSmall: { disabled: fieldTranslateDisabled },
        translateRightSmall: { disabled: fieldTranslateDisabled },
        rotateLeftByStep: { disabled: rotateSelectedFieldsDisabled },
        rotateRightByStep: { disabled: rotateSelectedFieldsDisabled },
      });
    }
    case UPDATE_CUSTOMIZE_ACTION:
      return updateActions(state, {
        [payload.name]: { ...payload.params },
      });
    default:
      return state;
  }
};

export const pendingActionReducer = (state = null, { type, payload: action = {} } = {}) => {
  switch (type) {
    case PERFORM_ACTION:
      return action;
    case CLEANUP_ACTION:
      return null;
    default:
      return state;
  }
};

export default combineReducers({
  pendingAction: pendingActionReducer,
  customizeData: customizeDataReducer,
  actions: actionsReducer,
});
